// src/supabase.js - VOLVER A LO BÁSICO QUE FUNCIONABA
import { createClient } from '@supabase/supabase-js';
import authHelper from './utils/authHelper';

// Verificar variables de entorno
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

console.log('🔗 Supabase URL:', supabaseUrl);

// Configuración ORIGINAL que funcionaba
const supabaseOptions = {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: window.localStorage,
    debug: process.env.NODE_ENV === 'development'
  },
  global: {
    headers: {
      'X-Client-Info': 'avlink-network'
    }
  }
};

// Crear cliente base de Supabase
const supabaseClient = createClient(supabaseUrl, supabaseAnonKey, supabaseOptions);

// Exportamos directamente el cliente para mantener todos los métodos nativos
const supabase = supabaseClient;

/**
 * 🔍 Obtiene el perfil completo del usuario autenticado - VERSIÓN ORIGINAL
 */
supabase.getFullUserProfile = async () => {
  try {
    console.log('🔍 [Supabase] Obteniendo perfil completo...');
    
    // PASO 1: Obtener usuario autenticado
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    
    if (authError) {
      console.error('❌ [Supabase] Error al obtener usuario:', authError);
      return null;
    }
    
    if (!user) {
      console.log('ℹ️ [Supabase] No hay usuario autenticado');
      return null;
    }
    
    console.log('✅ [Supabase] Usuario autenticado encontrado:', {
      id: user.id,
      email: user.email,
      emailConfirmed: !!user.email_confirmed_at
    });
    
    // PASO 2: Determinar tipo de usuario
    const isCompany = authHelper.isCompanyUser(user);
    const profileTable = isCompany ? 'company_profiles' : 'user_profiles';
    
    console.log('📋 [Supabase] Consultando tabla:', profileTable);
    
    // PASO 3: Obtener perfil específico
    const { data: profile, error: profileError } = await supabase
      .from(profileTable)
      .select('*')
      .eq('id', user.id)
      .maybeSingle();
    
    if (profileError) {
      console.error(`❌ [Supabase] Error obteniendo perfil de ${profileTable}:`, profileError);
      
      // Si el perfil no existe, devolver datos básicos del auth
      if (profileError.code === 'PGRST116') {
        console.warn(`⚠️ [Supabase] Perfil no encontrado en ${profileTable}, usando datos básicos`);
        return {
          ...user,
          user_type: isCompany ? 'company' : 'individual',
          is_company: isCompany,
          is_admin: false,
          profile_incomplete: true
        };
      }
      
      return null;
    }
    
    if (!profile) {
      console.warn(`⚠️ [Supabase] Perfil vacío en ${profileTable}`);
      return {
        ...user,
        user_type: isCompany ? 'company' : 'individual',
        is_company: isCompany,
        is_admin: false,
        profile_incomplete: true
      };
    }
    
    console.log('✅ [Supabase] Perfil encontrado:', {
      id: profile.id,
      email: profile.email,
      memberNumber: profile.member_number,
      membershipLevel: profile.membership_level || profile.subscription_tier
    });
    
    // PASO 4: Determinar privilegios de administrador
    const isAdmin = 
      (profile.is_admin === true) || 
      (profile.is_admin === 'true') || 
      (user.user_metadata?.is_admin === true) || 
      (user.user_metadata?.is_admin === 'true');
    
    // PASO 5: Crear objeto unificado
    const fullUserProfile = {
      ...user,
      ...profile,
      is_admin: isAdmin,
      user_type: isCompany ? 'company' : 'individual',
      is_company: isCompany,
      profile_complete: true
    };
    
    console.log('✅ [Supabase] Perfil completo construido:', {
      id: fullUserProfile.id,
      email: fullUserProfile.email,
      userType: fullUserProfile.user_type,
      isAdmin: fullUserProfile.is_admin,
      membershipLevel: fullUserProfile.membership_level || fullUserProfile.subscription_tier
    });
    
    return fullUserProfile;
    
  } catch (error) {
    console.error('❌ [Supabase] Error general en getFullUserProfile:', error);
    return null;
  }
};

/**
 * 🔍 Verifica si existe una sesión persistente válida - VERSIÓN ORIGINAL SIMPLE
 */
supabase.hasPersistedSession = async () => {
  try {
    console.log('🔍 [Supabase] Verificando sesión persistente...');
    
    // MÉTODO ORIGINAL: Verificar sesión actual de Supabase
    const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
    
    if (sessionError) {
      console.error('❌ [Supabase] Error obteniendo sesión:', sessionError);
      return false;
    }
    
    const hasValidSession = !!(
      sessionData && 
      sessionData.session && 
      sessionData.session.user && 
      sessionData.session.access_token
    );
    
    console.log('📊 [Supabase] Verificación de sesión:', {
      hasSession: hasValidSession,
      sessionExists: !!sessionData?.session,
      userExists: !!sessionData?.session?.user,
      hasAccessToken: !!sessionData?.session?.access_token,
      userId: sessionData?.session?.user?.id || 'none',
      email: sessionData?.session?.user?.email || 'none'
    });
    
    if (hasValidSession) {
      console.log('✅ [Supabase] Sesión válida encontrada');
      return true;
    }
    
    console.log('❌ [Supabase] No hay sesión persistente válida');
    return false;
    
  } catch (error) {
    console.error('❌ [Supabase] Error general en hasPersistedSession:', error);
    return false;
  }
};

/**
 * 🔄 Refresca la sesión actual - VERSIÓN ORIGINAL
 */
supabase.refreshSession = async () => {
  try {
    console.log('🔄 [Supabase] Refrescando sesión...');
    
    const { data, error } = await supabase.auth.refreshSession();
    
    if (error) {
      console.error('❌ [Supabase] Error refrescando sesión:', error);
      return { success: false, error };
    }
    
    console.log('✅ [Supabase] Sesión refrescada exitosamente:', {
      hasSession: !!data?.session,
      userId: data?.session?.user?.id || 'none'
    });
    
    return { success: true, data };
    
  } catch (error) {
    console.error('❌ [Supabase] Error general en refreshSession:', error);
    return { success: false, error };
  }
};

/**
 * 🧹 Limpia completamente la sesión local - VERSIÓN ORIGINAL
 */
supabase.clearSession = async () => {
  try {
    console.log('🧹 [Supabase] Limpiando sesión...');
    
    // 1. Cerrar sesión en Supabase
    const { error } = await supabase.auth.signOut();
    
    if (error) {
      console.error('❌ [Supabase] Error cerrando sesión:', error);
    }
    
    // 2. Limpiar localStorage manualmente
    const keysToRemove = Object.keys(localStorage).filter(key => 
      key.includes('supabase') || 
      key.includes('sb-') ||
      key.includes('avlink')
    );
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      console.log('🗑️ [Supabase] Eliminado de localStorage:', key);
    });
    
    // 3. Limpiar sessionStorage también
    const sessionKeysToRemove = Object.keys(sessionStorage).filter(key => 
      key.includes('supabase') || 
      key.includes('sb-') ||
      key.includes('avlink')
    );
    
    sessionKeysToRemove.forEach(key => {
      sessionStorage.removeItem(key);
      console.log('🗑️ [Supabase] Eliminado de sessionStorage:', key);
    });
    
    console.log('✅ [Supabase] Sesión limpiada completamente');
    return true;
    
  } catch (error) {
    console.error('❌ [Supabase] Error limpiando sesión:', error);
    return false;
  }
};

// 🔗 Configurar listener global para debugging (solo en desarrollo)
if (process.env.NODE_ENV === 'development') {
  supabase.auth.onAuthStateChange((event, session) => {
    console.log('🔔 [Supabase] Auth State Change:', {
      event,
      hasSession: !!session,
      userId: session?.user?.id || null,
      email: session?.user?.email || null
    });
  });
  
  // Exponer funciones de debugging globalmente
  window.supabaseDebug = {
    hasPersistedSession: supabase.hasPersistedSession,
    getFullUserProfile: supabase.getFullUserProfile,
    clearSession: supabase.clearSession,
    refreshSession: supabase.refreshSession
  };
  
  console.log('🛠️ [Supabase] Funciones de debug disponibles en window.supabaseDebug');
}

export default supabase;